import type {
  Place,
  PlaceChanges,
  PlaceInput,
  PlaceStatus,
  PlaceValidationErrors,
  ReturnIntent,
  StationIdCollection,
} from './types';

export interface CreatePlaceOptions {
  id: string;
  /** Current time as an ISO 8601 string. Passed in to keep this module deterministic. */
  now: string;
}

export interface UpdatePlaceOptions {
  /** Current time as an ISO 8601 string. Passed in to keep this module deterministic. */
  now: string;
}

export interface NormalizedPlaceInput {
  name: string;
  yandexMapsUrl: string;
  stationId: string;
  status: PlaceStatus;
  returnIntent: ReturnIntent;
  tags: string[];
  comment: string;
}

const YANDEX_DOMAINS = new Set([
  'yandex.ru',
  'yandex.com',
  'yandex.by',
  'yandex.kz',
  'yandex.uz',
  'yandex.com.tr',
]);

const PLACE_STATUSES = new Set<PlaceStatus>(['want', 'visited']);
const RETURN_INTENTS = new Set<Exclude<ReturnIntent, null>>([
  'yes',
  'no',
  'unsure',
]);

export class PlaceValidationError extends Error {
  readonly errors: PlaceValidationErrors;

  constructor(errors: PlaceValidationErrors) {
    super('Данные места заполнены некорректно.');
    this.name = 'PlaceValidationError';
    this.errors = errors;
  }
}

function compactWhitespace(value: string): string {
  return value.trim().replace(/\s+/gu, ' ');
}

function canonicalText(value: string): string {
  return compactWhitespace(value)
    .normalize('NFKC')
    .toLocaleLowerCase('ru-RU')
    .replaceAll('ё', 'е');
}

export function normalizeTags(tags: readonly string[] | undefined): string[] {
  const result: string[] = [];
  const seen = new Set<string>();

  for (const sourceTag of tags ?? []) {
    const tag = compactWhitespace(sourceTag);
    const key = canonicalText(tag);

    if (key.length === 0 || seen.has(key)) {
      continue;
    }

    seen.add(key);
    result.push(tag);
  }

  return result;
}

function parseYandexMapsUrl(value: string): URL | null {
  let url: URL;

  try {
    url = new URL(value.trim());
  } catch {
    return null;
  }

  if (url.protocol !== 'https:' || url.username !== '' || url.password !== '') {
    return null;
  }

  const hostname = url.hostname.toLocaleLowerCase('en-US');
  const bareHostname = hostname.startsWith('www.')
    ? hostname.slice('www.'.length)
    : hostname;

  if (bareHostname.startsWith('maps.')) {
    return YANDEX_DOMAINS.has(bareHostname.slice('maps.'.length)) ? url : null;
  }

  if (!YANDEX_DOMAINS.has(bareHostname)) {
    return null;
  }

  return /^\/maps(?:\/|$)/u.test(url.pathname) ? url : null;
}

export function isYandexMapsUrl(value: string): boolean {
  return parseYandexMapsUrl(value) !== null;
}

/** Normalises harmless URL formatting while keeping all route/place parameters intact. */
export function normalizeYandexMapsUrl(value: string): string {
  const parsed = parseYandexMapsUrl(value);
  return parsed?.toString() ?? value.trim();
}

/** A comparison-only representation used for duplicate warnings. */
export function canonicalizeYandexMapsUrl(value: string): string | null {
  const parsed = parseYandexMapsUrl(value);

  if (parsed === null) {
    return null;
  }

  parsed.hostname = parsed.hostname.toLocaleLowerCase('en-US').replace(/^www\./u, '');

  for (const key of [...parsed.searchParams.keys()]) {
    const lowerKey = key.toLocaleLowerCase('en-US');
    if (lowerKey.startsWith('utm_') || lowerKey === 'yclid' || lowerKey === 'gclid') {
      parsed.searchParams.delete(key);
    }
  }

  parsed.searchParams.sort();

  if (parsed.pathname.length > 1 && parsed.pathname.endsWith('/')) {
    parsed.pathname = parsed.pathname.slice(0, -1);
  }

  return parsed.toString();
}

function collectionHasStation(
  stationIds: StationIdCollection,
  stationId: string,
): boolean {
  if (Array.isArray(stationIds)) {
    return stationIds.some((station) => station.id === stationId);
  }

  return (stationIds as ReadonlySet<string>).has(stationId);
}

export function validatePlaceInput(
  input: PlaceInput,
  stationIds?: StationIdCollection,
): PlaceValidationErrors {
  const errors: PlaceValidationErrors = {};
  const name = typeof input.name === 'string' ? compactWhitespace(input.name) : '';
  const url = typeof input.yandexMapsUrl === 'string' ? input.yandexMapsUrl : '';
  const stationId =
    typeof input.stationId === 'string' ? compactWhitespace(input.stationId) : '';

  if (name.length === 0) {
    errors.name = 'Введите название места.';
  }

  if (url.trim().length === 0) {
    errors.yandexMapsUrl = 'Вставьте ссылку на место в Яндекс Картах.';
  } else if (!isYandexMapsUrl(url)) {
    errors.yandexMapsUrl =
      'Введите корректную HTTPS-ссылку Яндекс Карт, например https://yandex.ru/maps/…';
  }

  if (stationId.length === 0) {
    errors.stationId = 'Выберите станцию метро из списка.';
  } else if (stationIds !== undefined && !collectionHasStation(stationIds, stationId)) {
    errors.stationId = 'Выберите станцию метро из списка.';
  }

  if (!PLACE_STATUSES.has(input.status)) {
    errors.status = 'Выберите статус места.';
  }

  const returnIntent = input.returnIntent ?? null;
  if (returnIntent !== null && !RETURN_INTENTS.has(returnIntent)) {
    errors.returnIntent = 'Выберите один из предложенных вариантов.';
  }

  return errors;
}

export function hasPlaceValidationErrors(errors: PlaceValidationErrors): boolean {
  return Object.keys(errors).length > 0;
}

export function normalizePlaceInput(input: PlaceInput): NormalizedPlaceInput {
  const status = input.status;

  return {
    name: compactWhitespace(input.name),
    yandexMapsUrl: normalizeYandexMapsUrl(input.yandexMapsUrl),
    stationId: compactWhitespace(input.stationId),
    status,
    returnIntent: status === 'want' ? null : (input.returnIntent ?? null),
    tags: normalizeTags(input.tags),
    comment: input.comment?.trim() ?? '',
  };
}

function assertTimestamp(value: string, field: string): void {
  if (
    typeof value !== 'string' ||
    value.trim() !== value ||
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:\d{2})$/u.test(value) ||
    Number.isNaN(Date.parse(value))
  ) {
    throw new TypeError(`${field} должен быть датой в формате ISO 8601.`);
  }
}

function assertInputIsValid(input: PlaceInput): void {
  const errors = validatePlaceInput(input);
  if (hasPlaceValidationErrors(errors)) {
    throw new PlaceValidationError(errors);
  }
}

export function createPlace(
  input: PlaceInput,
  options: CreatePlaceOptions,
): Place {
  assertInputIsValid(input);
  assertTimestamp(options.now, 'now');

  const id = compactWhitespace(options.id);
  if (id.length === 0) {
    throw new TypeError('id места не может быть пустым.');
  }

  const normalised = normalizePlaceInput(input);

  return {
    id,
    ...normalised,
    createdAt: options.now,
    updatedAt: options.now,
  };
}

export function updatePlace(
  current: Place,
  changes: PlaceChanges,
  options: UpdatePlaceOptions,
): Place {
  assertTimestamp(options.now, 'now');

  const merged: PlaceInput = {
    name: changes.name ?? current.name,
    yandexMapsUrl: changes.yandexMapsUrl ?? current.yandexMapsUrl,
    stationId: changes.stationId ?? current.stationId,
    status: changes.status ?? current.status,
    returnIntent:
      changes.returnIntent === undefined
        ? current.returnIntent
        : changes.returnIntent,
    tags: changes.tags ?? current.tags,
    comment: changes.comment ?? current.comment,
  };

  assertInputIsValid(merged);
  const normalised = normalizePlaceInput(merged);

  return {
    id: current.id,
    ...normalised,
    createdAt: current.createdAt,
    updatedAt: options.now,
  };
}

export function findDuplicatePlace(
  places: readonly Place[],
  yandexMapsUrl: string,
  excludePlaceId?: string,
): Place | undefined {
  const target = canonicalizeYandexMapsUrl(yandexMapsUrl);
  if (target === null) {
    return undefined;
  }

  return places.find(
    (place) =>
      place.id !== excludePlaceId &&
      canonicalizeYandexMapsUrl(place.yandexMapsUrl) === target,
  );
}

export function hasDuplicateYandexMapsUrl(
  places: readonly Place[],
  yandexMapsUrl: string,
  excludePlaceId?: string,
): boolean {
  return findDuplicatePlace(places, yandexMapsUrl, excludePlaceId) !== undefined;
}
